import { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Phone } from "lucide-react";
import { Seo, breadcrumbSchema } from "../components/site/Seo";
import { PageHero } from "../components/site/PageHero";
import { LocationSection } from "../components/site/Location";
import { api, errorText, newRequestId } from "../lib/api";
import { SITE, TEL } from "../lib/site";

function ContactForm() {
  const [f, setF] = useState({ nom: "", telephone: "", email: "", message: "", consentement: false, website: "" });
  const [rid] = useState(newRequestId);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leads", { ...f, type: "contact", email: f.email || null, request_id: rid });
      setSent(true);
      toast.success("Message envoyé.");
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  if (sent) return <p className="rounded-2xl bg-ag-mist p-6 font-semibold text-ag-navy" data-testid="contact-success">Merci, votre message a bien été envoyé. Nous vous répondons rapidement.</p>;
  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2" data-testid="contact-form">
      <input className="field" placeholder="Nom *" required value={f.nom} onChange={set("nom")} aria-label="Nom" data-testid="contact-name" />
      <input className="field" type="tel" placeholder="Téléphone *" required value={f.telephone} onChange={set("telephone")} aria-label="Téléphone" data-testid="contact-phone" />
      <input className="field sm:col-span-2" type="email" placeholder="Email" value={f.email} onChange={set("email")} aria-label="Email" data-testid="contact-email" />
      <textarea className="field min-h-[140px] sm:col-span-2" placeholder="Votre message *" required value={f.message} onChange={set("message")} aria-label="Message" data-testid="contact-message" />
      <input className="hidden" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} aria-hidden="true" />
      <label className="flex items-start gap-2 text-xs text-slate-600 sm:col-span-2">
        <input type="checkbox" required checked={f.consentement} onChange={set("consentement")} className="mt-0.5" data-testid="contact-consent" />
        J'accepte que mes données soient utilisées pour répondre à ma demande.
      </label>
      <button disabled={busy} className="btn btn-blue justify-center sm:col-span-2 sm:justify-self-start" data-testid="contact-submit">{busy ? "Envoi…" : "Envoyer mon message"}</button>
    </form>
  );
}

export default function Contact() {
  const crumbs = [{ to: "/", label: "Accueil" }, { to: "/contact", label: "Contact" }];
  return (
    <>
      <Seo title="Contact A+ Glass Haguenau | 03 88 93 16 73 | 101A Route de Marienthal" description="Contactez A+GLASS Pare-Brise HAGUENAU : 03 88 93 16 73, 101A Route de Marienthal 67500 Haguenau. Devis, rendez-vous, rappel gratuit." path="/contact" schemas={[breadcrumbSchema(crumbs)]} />
      <PageHero eyebrow="Contact" title="Contacter A+ Glass Haguenau" crumbs={crumbs} intro="Par téléphone, par email ou via le formulaire : notre équipe vous répond pendant nos horaires d'ouverture." />
      <section className="px-5 py-16 lg:px-8">
        <div className="mx-auto grid max-w-[1320px] gap-10 lg:grid-cols-12">
          <div className="space-y-3 lg:col-span-5">
            <a href={TEL} className="flex items-center gap-4 rounded-2xl bg-ag-blue p-6 text-white" data-testid="contact-phone-card"><Phone className="h-6 w-6" /><span className="font-display text-2xl font-extrabold">{SITE.phone}</span></a>
            <a href={`mailto:${SITE.email}`} className="flex items-center gap-4 rounded-2xl bg-white p-6 ring-1 ring-slate-200" data-testid="contact-email-card"><Mail className="h-6 w-6 text-ag-blue" /><span className="font-semibold text-ag-ink">{SITE.email}</span></a>
            <a href={SITE.mapsDir} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 rounded-2xl bg-white p-6 ring-1 ring-slate-200" data-testid="contact-address-card"><MapPin className="h-6 w-6 text-ag-blue" /><span className="font-semibold text-ag-ink">{SITE.street}, {SITE.zip} {SITE.city}</span></a>
          </div>
          <div className="rounded-[1.75rem] bg-white p-7 ring-1 ring-slate-200 sm:p-10 lg:col-span-7">
            <h2 className="mb-6 font-display text-2xl font-extrabold text-ag-ink">Écrivez-nous</h2>
            <ContactForm />
          </div>
        </div>
      </section>
      <LocationSection />
    </>
  );
}
